<template>
  <div
    v-if="!isEditMode"
    :class="[
      'owc-hotspot-sidepanel',
      'wb-grid-col-mq3-5',
      { 'owc-hotspot-sidepanel--closed': !sidePanelOpen },
    ]"
    :style="{ '--translate-direction': isRtl ? -1 : 1 }"
  >
    <div class="owc-hotspot-sidepanel__base">
      <button
        type="button"
        aria-label="Close"
        class="owc-hotspot-sidepanel__close wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
        @click="closeSidebar"
      >
        <wb-icon class="wb-round-button__icon" aria-hidden="true" name="bds/close/24" flip-rtl></wb-icon>
      </button>
      <template v-if="isMobile">
        <wb-slider
          show-dot-navigation
          scroll-snap
          ref="sidepanelSlider"
          class="owc-hotspot-sidepanel__slider"
          :initial-item="normalizedCurrentPage"
          @wbchange="stopVideoWhenSlidingOnMobile"
          @wbchange.once="mobileSlideChanged"
        >
          <HotspotSidepanelItem
            v-for="hotspot in hotspots"
            :key="hotspot.id"
            :item="hotspot.sidepanel"
            :mobile="isMobile"
            class="wb-slider__item"
          />
        </wb-slider>
      </template>
      <template v-else>
        <transition-group
          name="owc-sidepanel-item-move-in-out"
          mode="in-out"
          :enter-class="
            isBackwardsAnimation
              ? 'owc-sidepanel-item-move-in-out-enter-backwards'
              : 'owc-sidepanel-item-move-in-out-enter-forwards'
          "
          :leave-to-class="
            isBackwardsAnimation
              ? 'owc-sidepanel-item-move-in-out-leave-to-backwards'
              : 'owc-sidepanel-item-move-in-out-leave-to-forwards'
          "
        >
          <HotspotSidepanelItem
            v-if="activeHotspot"
            :key="activeHotspot.id"
            :item="activeSidepanel"
            :isActive="sidePanelOpen"
          />
        </transition-group>
        <div v-if="hotspots.length > 1" class="owc-hotspot-sidepanel__nav-buttons">
          <div class="owc-hotspot-sidepanel__nav-buttons-column">
            <button
              class="wb-round-button wb-round-button--solid-level-0 wb-round-button--medium owc-hotspot-sidepanel__nav-buttons-button"
              :aria-label="leftNavButtonLabel"
              @click="activateLeftHotspot"
            >
              <wb-icon
                class="wb-round-button__icon"
                aria-hidden="true"
                name="bds/chevron-left/24"
                flip-rtl
              ></wb-icon>
              {{ leftNavButtonLabel }}
            </button>
            <span class="wb-type-hint owc-hotspot-sidepanel__nav-buttons-button-hint">{{
              leftNavButtonLabel
            }}</span>
          </div>
          <div class="owc-hotspot-sidepanel__nav-buttons-column">
            <button
              class="wb-round-button wb-round-button--solid-level-0 wb-round-button--medium owc-hotspot-sidepanel__nav-buttons-button"
              :aria-label="rightNavButtonLabel"
              @click="activateRightHotspot"
            >
              <wb-icon
                class="wb-round-button__icon"
                aria-hidden="true"
                name="bds/chevron-right/24"
                flip-rtl
              ></wb-icon>
              {{ rightNavButtonLabel }}
            </button>
            <span class="wb-type-hint owc-hotspot-sidepanel__nav-buttons-button-hint">{{
              rightNavButtonLabel
            }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  HOTSPOTSIMPLE_SIDEPANEL_CLOSE_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_LEFT_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_RIGHT_EVENT,
  HOTSPOTSIMPLE_SIDEPANEL_MOUNTED_EVENT,
  HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS,
  HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS,
} from '@owc-packages/Hotspot/src/Hotspot.config';
import { HotspotSidepanelComponent } from '@owc-packages/Hotspot/src/HotspotSidepanel';
import { mobileBreakpoints } from '@owc-packages/_utils/MediaQueryService';
import { ScrollLockService } from '@workbench/core';
import HotspotSidepanelItem from './HotspotSidepanelItem.vue';
import ResizeEvent from '@owc-utils/Mixins/ResizeEvent';
import AEM from '@owc-utils/Mixins/AEM';

const scrollLockService = new ScrollLockService();

/**
 * TODO: Documentation
 * Hotspot Sidepanel
 *
 * A Sidepanel to be placed in the HotspotSimpleItem.
 */
export default (Vue as HotspotSidepanelComponent).extend({
  name: 'owc-hotspot-sidepanel',
  mixins: [ResizeEvent, AEM],
  props: {
    sidePanelOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    hotspots: {
      type: Array,
      required: true,
      default: () => [],
    },
    activeHotspot: {
      type: Object,
      default: null,
    },
    leftHotspot: {
      type: Object,
      default: null,
    },
    rightHotspot: {
      type: Object,
      default: null,
    },
    isRtl: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HotspotSidepanelItem,
  },
  data: () => ({
    isMobile: false,
    isBackwardsAnimation: false,
  }),
  mounted() {
    this.onMQChange?.(this.setIsMobileFlag);
    this.setIsMobileFlag();
    this.$emit(HOTSPOTSIMPLE_SIDEPANEL_MOUNTED_EVENT);
    if (this.isMobile) {
      scrollLockService.lock();
      this.$nextTick(() => this.scrollMobileSliderToInitialPage(this.$refs.sidepanelSlider));
    }
  },
  beforeDestroy() {
    if (this.isMobile) {
      scrollLockService.unlock();
    }
  },
  computed: {
    leftNavButtonLabel() {
      return this.leftHotspot ? this.leftHotspot.hotspotText : '';
    },
    rightNavButtonLabel() {
      return this.rightHotspot ? this.rightHotspot.hotspotText : '';
    },
    dotNavCurrentPage() {
      return this.hotspots.findIndex(hotspot => hotspot.id === this.activeHotspot?.id) || 0;
    },
    normalizedCurrentPage() {
      return this.dotNavCurrentPage + 1;
    },
    activeSidepanel() {
      return this.activeHotspot?.sidepanel;
    },
  },
  methods: {
    // Because of the way the tooltip (on the workbench side) is implemented
    // we need to move the hotspot to is correct position first before we
    // activate it.
    // It is necessary to pass the click event to the parent. The parent will handle
    // the activation and diactivation of the correct hotspot item.
    // https://jira.mercedes-benz.io/browse/CINOWF-30484
    activateLeftHotspot() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_LEFT_EVENT);
    },
    activateRightHotspot() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_RIGHT_EVENT);
    },
    closeSidebar() {
      this.$emit(HOTSPOTSIMPLE_SIDEPANEL_CLOSE_EVENT);
    },
    setIsMobileFlag() {
      const currentMq = this.getCurrentMediaQuery?.();
      this.isMobile = Boolean(currentMq && mobileBreakpoints.includes(currentMq));
    },
    scrollMobileSliderToInitialPage(sliderEl) {
      const observer = new MutationObserver(() => {
        const sliderInnerEl: HTMLElement | null = sliderEl.querySelector(
          HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS,
        );
        if (sliderInnerEl) {
          const sliderItemEl = sliderInnerEl.querySelector(
            `${HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS}:nth-child(${this.normalizedCurrentPage})`,
          ) as HTMLElement;
          sliderInnerEl.style.scrollBehavior = 'initial';
          sliderInnerEl.scrollLeft = sliderItemEl?.offsetLeft || 0;
          sliderInnerEl.removeAttribute('style');
          observer.disconnect();
        }
      });
      observer.observe(sliderEl, { childList: true });
    },
    toggleBackwardsAnimation(currentActiveHotspot, previousActiveHotspot) {
      const lastItemIndex = this.hotspots.length - 1;
      const { currentIndex, previousIndex } = this.hotspots.reduce(
        (indexes, hotspot, index) => {
          if (hotspot.id === currentActiveHotspot.id) {
            indexes.currentIndex = index;
          }
          if (hotspot.id === previousActiveHotspot.id) {
            indexes.previousIndex = index;
          }
          return indexes;
        },
        {
          currentIndex: 0,
          previousIndex: 0,
        },
      );

      // Slide from last to first
      if (currentIndex === 0 && previousIndex === lastItemIndex) {
        this.isBackwardsAnimation = true;
        return;
      }

      // Slide from first to last
      if (currentIndex === lastItemIndex && previousIndex === 0) {
        this.isBackwardsAnimation = false;
        return;
      }

      this.isBackwardsAnimation = currentIndex - previousIndex > 0;
    },
    stopVideoWhenSlidingOnMobile() {
      const wrapper = this.$refs.sidepanelSlider;
      const playButton = wrapper.querySelector('.plyr--playing [data-plyr="play"]') as HTMLButtonElement;
      if (playButton) {
        playButton.click();
      }
    },
    // OWCA-1099. The idea is as follows: mobileSlideChanged listener is added
    // once to give us the opportunity to register another listener to detect
    // scroll position of the slider; when snap point is reached, which means
    // new slide is fully visible and previous fully hidden, we iterate through
    // scrollable elements and call scrollItemContentToTop() which detects
    // whether slide is fully hidden to call scrollTo() onto it.
    mobileSlideChanged() {
      this.stopVideoWhenSlidingOnMobile();
      const wrapper = this.$refs.sidepanelSlider;
      const sliderInnerEl = wrapper.querySelector(HOTSPOT_SIDEPANEL_MOBILE_SLIDER_INNER_CLASS);
      if (sliderInnerEl) {
        sliderInnerEl.addEventListener('scroll', (event: Event): void => {
          const target = event.target as HTMLElement;
          // Snap point is reached, so loop through items and scroll content to top if needed
          if (target.scrollLeft % target.offsetWidth === 0) {
            this.$refs.sidepanelSlider
              .querySelectorAll(HOTSPOT_SIDEPANEL_MOBILE_SLIDER_ITEM_CLASS)
              .forEach(this.scrollItemContentToTop);
          }
        });
      }
    },
    scrollItemContentToTop(item) {
      if (item.scrollTop > 0) {
        const { left, width } = item.getBoundingClientRect();
        if (left < 0 || left >= width) {
          item.scrollTo(0, 0);
        }
      }
    },
  },
  watch: {
    activeHotspot: 'toggleBackwardsAnimation',
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/rtl-utils';
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/grid';

$moduleName: 'owc-hotspot-sidepanel';
$z-index-base: $z-index-overlay;
$z-index-close: $z-index-overlay;

.#{$moduleName} {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: $z-index-base;

  overflow-x: hidden;

  background: var(--wb-white);

  transform: translateZ(10000px);

  @include breakpoint-from(mq1) {
    width: 100%;
  }
  @include breakpoint-from(mq3) {
    position: absolute;
  }

  // overwriting wb grid padding, since only the width of the column is needed
  [dir='ltr'] & {
    &.wb-grid-col-mq3-5 {
      padding-right: 0;
      padding-left: 0;
    }
  }
  &__base {
    height: 100%;

    overflow-y: auto;
  }
  &__slider {
    position: relative;

    height: 100%;

    .wb-slider {
      &__outer,
      &__inner {
        height: 100%;
      }
      &__item {
        width: 100%;
        height: 100%;
        overflow-y: auto;
      }
    }
    wb-dot-navigation {
      position: absolute;
      bottom: 0;
      left: 0;

      width: 100%;
      margin-top: 0;
      padding: var(--wb-spacing-m) 0 var(--wb-spacing-s);

      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0, var(--wb-white) var(--wb-spacing-m));
    }
  }
  &__close {
    position: absolute;
    top: var(--wb-spacing-xs);
    right: var(--wb-spacing-xs);

    z-index: $z-index-close;

    color: var(--wb-grey-20);

    wb-icon {
      pointer-events: none;
    }
  }
  &__nav-buttons {
    position: absolute;

    bottom: 0;
    left: 0;

    display: none;
    justify-content: space-between;
    width: 100%;
    padding-top: var(--wb-spacing-xs);

    @include breakpoint-from(mq3) {
      display: flex;
    }

    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0, var(--wb-white) var(--wb-spacing-s));
  }
  &__nav-buttons-column {
    display: flex;
    align-items: center;
    width: 50%;

    &:last-child {
      flex-direction: row-reverse;
      justify-content: flex-start;

      .#{$moduleName}__nav-buttons-button-hint {
        text-align: end;
      }
    }
  }
  &__nav-buttons-button {
    margin: var(--wb-spacing-xxs);

    &-hint {
      flex: 1;

      opacity: 0;

      transition: opacity 0.15s $wb-fade;
    }
    &:hover ~ &-hint {
      opacity: 1;
    }
  }
  &--closed {
    width: 0;
  }
  &--closed &__close {
    display: none;
  }
  .owc-sidepanel-item-move-in-out {
    &-leave,
    &-enter-to {
      transform: translateX(0);
      opacity: 1;
    }
    &-enter-forwards {
      transform: translateX(calc(var(--translate-direction) * -100%));
      opacity: 0;
    }
    &-leave-to-forwards {
      transform: translateX(calc(var(--translate-direction) * 100%));
      opacity: 0;
    }
    &-enter-backwards {
      transform: translateX(calc(var(--translate-direction) * 100%));
      opacity: 0;
    }
    &-leave-to-backwards {
      transform: translateX(calc(var(--translate-direction) * -100%));
      opacity: 0;
    }
    &-enter-active,
    &-leave-active {
      transition: transform 0.3s $wb-open, opacity 0.3s 0.1s $wb-fade;
    }
  }
}

@include rtl-rule('.owc-hotspot-sidepanel__close', false) {
  right: unset;
  left: var(--wb-spacing-xs);
}
@include rtl-rule('.owc-hotspot-sidepanel', false) {
  right: unset;
  left: 0;
}
</style>
